import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";

/* =============================================================
   TASK: PointOnGraphYesNoTask
   Cel: Sprawdź, czy punkt (x0, y0) należy do wykresu funkcji f.
   Odpowiedź: TAK / NIE (SelectInput)
   Założenie: funkcja liniowa lub kwadratowa (prosto na start)
============================================================= */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

function sampleNonTrivialX() {
  const mag = randInt(2, 6);
  return Math.random() < 0.5 ? -mag : mag;
}

function formatLinearLatex(a, b) {
  const aStr = a === 1 ? "" : a === -1 ? "-" : String(a);
  const bStr = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
  return `${aStr}x${bStr}`;
}

function formatQuadraticLatex(a, b, c) {
  const aStr = a === 1 ? "" : a === -1 ? "-" : String(a);
  const bStr = b === 0 ? "" : b > 0 ? ` + ${b}x` : ` - ${Math.abs(b)}x`;
  const cStr = c === 0 ? "" : c > 0 ? ` + ${c}` : ` - ${Math.abs(c)}`;
  return `${aStr}x^2${bStr}${cStr}`;
}

export function generateTask() {
  const isQuadratic = Math.random() < 0.35;

  const x0 = sampleNonTrivialX();

  let data;

  if (isQuadratic) {
    const a = randInt(-3, 3, new Set([0]));
    const b = randInt(-5, 5);
    const c = randInt(-6, 6);

    const yTrue = a * x0 * x0 + b * x0 + c;

    const shouldBelong = Math.random() < 0.5; // 50/50
    const y0 = shouldBelong ? yTrue : yTrue + randInt(1, 6) * (Math.random() < 0.5 ? 1 : -1);

    data = {
      kind: "quadratic",
      a,
      b,
      c,
      x0,
      y0,
      belongs: shouldBelong,
    };
  } else {
    const a = randInt(-5, 5, new Set([0]));
    const b = randInt(-8, 8);

    const yTrue = a * x0 + b;

    const shouldBelong = Math.random() < 0.5; // 50/50
    const y0 = shouldBelong ? yTrue : yTrue + randInt(1, 8) * (Math.random() < 0.5 ? 1 : -1);

    data = {
      kind: "linear",
      a,
      b,
      x0,
      y0,
      belongs: shouldBelong,
    };
  }

  return { type: "PointOnGraphYesNoTask", data };
}

export function renderTask({ data }) {
  const { kind, a, b, c, x0, y0 } = data;

  const latex =
    kind === "linear"
      ? formatLinearLatex(a, b)
      : formatQuadraticLatex(a, b, c);

  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest funkcja:
        <BlockMath math={`f(x) = ${latex}`} />
      </div>
      <div>
        Sprawdź, czy punkt <InlineMath math={`(${x0},\\,${y0})`} /> należy do wykresu tej funkcji.
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = typeof userAnswer === "string" ? userAnswer : (userAnswer?.value ?? "");

  return (
    <div style={{ marginTop: 10, maxWidth: 220 }}>
      <SelectInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        options={[
          { value: "TAK", label: "TAK" },
          { value: "NIE", label: "NIE" },
        ]}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ans = typeof userAnswer === "string" ? userAnswer : userAnswer?.value;
  if (!ans) return false;

  const correct = data.belongs ? "TAK" : "NIE";
  return ans === correct;
}

export function renderAnswer({ data }) {
  const correct = data.belongs ? "TAK" : "NIE";
  return <InlineMath math={`\\text{Odpowiedź: } ${correct}`} />;
}

export function renderExplanation({ data }) {
  const { kind, a, b, c, x0, y0, belongs } = data;

  const yCalc =
    kind === "linear"
      ? a * x0 + b
      : a * x0 * x0 + b * x0 + c;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Podstawiamy argument <InlineMath math={`x=${x0}`} /> do wzoru funkcji.</div>

      {kind === "linear" ? (
        <BlockMath math={`f(${x0}) = ${a}\\cdot ${x0} ${b >= 0 ? "+ " + b : "- " + Math.abs(b)} = ${yCalc}`} />
      ) : (
        <BlockMath math={`f(${x0}) = ${a}\\cdot ${x0}^2 ${b >= 0 ? "+ " + b + "\\cdot " + x0 : "- " + Math.abs(b) + "\\cdot " + x0} ${c >= 0 ? "+ " + c : "- " + Math.abs(c)} = ${yCalc}`} />
      )}

      <div><strong>Krok 2.</strong> Porównujemy z podaną drugą współrzędną punktu.</div>
      <div>
        Mamy punkt <InlineMath math={`(${x0},\\,${y0})`} />.
        Ponieważ <InlineMath math={`f(${x0}) = ${yCalc}`} />{" "}
        {belongs ? "zgadza się z" : "nie zgadza się z"} <InlineMath math={`${y0}`} />,
        więc odpowiedź to <strong>{belongs ? "TAK" : "NIE"}</strong>.
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const ans = typeof userAnswer === "string" ? userAnswer : userAnswer?.value;
  if (!ans) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\text{Twoja odpowiedź: } ${ans}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
